import { useCallback, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import TextArea from '../common/TextArea'
import {
  setBothLegalNoticeChecked,
  setCustomLicenseDocument,
  setCustomLicenseNumber,
  setLicenseDocument,
  setLicenseNumber,
  setLicenseReview,
  setRecommendLicenseDocument,
  setRecommendLicenseNumber,
} from './basicCreativeSlice'
import NoneAnchor from '../../../../components/NoneAnchor'
import { useConfirm } from '../../../dialog/Confirm'
import { useHistory, useParams } from 'react-router-dom'
import SelectBox from '../../../../components/form/SelectBox'
import Moment from 'react-moment'
import HelpTooltip from '../../../../components/tooltip/HelpTooltip'
import { isNil } from 'lodash'
import { GitbookLinks } from '../../../../utils/constants'
import CheckBox from '../../../../components/form/CheckBox'
import ErrorText from '../../../../components/form/ErrorText'

const TalkChannelLegalNotice = ({ disabled, errors }) => {
  const dispatch = useDispatch()
  const {
    basicCreative: {
      licenseNumber,
      licenseDocument,
      license,
      bothLegalNoticeChecked,
    },
    buildCreative: { channel },
  } = useSelector(state => state.creative, shallowEqual)
  const { accountId } = useParams()
  const [DocumentLinkConfirm, showDocumentLinkConfirm] = useConfirm()
  const history = useHistory()

  const handleLicenseNumberChange = useCallback(
    value => {
      dispatch(setLicenseNumber(value))
    },
    [dispatch]
  )
  const handleLicenseDocumentChange = useCallback(
    value => {
      dispatch(setLicenseDocument(value))
    },
    [dispatch]
  )
  const handleCustomLicenseNumberChange = useCallback(
    value => {
      dispatch(setCustomLicenseNumber(value))
    },
    [dispatch]
  )
  const handleCustomLicenseDocumentChange = useCallback(
    value => {
      dispatch(setCustomLicenseDocument(value))
    },
    [dispatch]
  )
  const handleRecommendLicenseNumberChange = useCallback(
    value => {
      dispatch(setRecommendLicenseNumber(value))
    },
    [dispatch]
  )
  const handleRecommendLicenseDocumentChange = useCallback(
    value => {
      dispatch(setRecommendLicenseDocument(value))
    },
    [dispatch]
  )
  const handleBothLegalNoticeChange = useCallback(
    value => {
      dispatch(setBothLegalNoticeChecked(value))
      dispatch(setLicenseNumber(''))
      dispatch(setLicenseDocument(null))
      dispatch(setRecommendLicenseNumber(''))
      dispatch(setRecommendLicenseDocument(null))
      dispatch(setCustomLicenseNumber(''))
      dispatch(setCustomLicenseDocument(null))
    },
    [dispatch]
  )

  useEffect(() => {
    dispatch(setLicenseReview(true))
  }, [dispatch])

  return (
    <>
      <div className="add_material">
        <div className="material_item">
          <div className="tit_itemwrap">
            <strong className="tit_item off in_active">법적고지</strong>
            <HelpTooltip nowrap={false}>
              법정사전심의 또는 자율심의 대상이거나, 의무 고지사항이 있는
              경우에는 각 협회의 심의필 번호 또는 각 업종별 의무 고지사항 등을
              기재하여야 합니다.{' '}
              <em className="fc_warn">
                심의 면제 대상이 아니라면 미입력시 심사가 승인되지 않을 수
                있습니다.
              </em>{' '}
              자세한 사항은{' '}
              <a
                href={GitbookLinks.TALK_CHANNEL_INDUSTRY}
                target="_blank"
                className="link_txt2"
                rel="noopener noreferrer">
                심사가이드
              </a>
              를 참고해주세요.
            </HelpTooltip>
          </div>
          <div className="desc_item">
            <span className="txt_item">
              법적고지가 필수인 업종은 광고와 함께 노출되는 고지문구 입력이
              필요합니다.
            </span>
          </div>
        </div>
      </div>

      <div className="cont_material material_type2">
        <div className="group_material">
          <div className="box_material">
            <div className="inner_material">
              <strong className="screen_out">법적고지 설정</strong>
              <div className="material_item">
                <CheckBox
                  disabled={disabled}
                  label={
                    <>
                      법적고지문구 개별 입력
                      <HelpTooltip nowrap={false}>
                        개별 입력을 활성화할 경우 추천/맞춤소재 별
                        법적고지문구와 소재심사서류 등록이 필수입니다.
                      </HelpTooltip>
                    </>
                  }
                  checked={bothLegalNoticeChecked}
                  onCheckedChange={checked => {
                    handleBothLegalNoticeChange(checked)
                  }}
                />
                {bothLegalNoticeChecked ? (
                  <>
                    <div className="group_itemwrap">
                      <div className="tit_itemwrap">
                        <strong className="tit_item">추천소재</strong>
                      </div>
                      <div className="desc_item">
                        <div className="box_item">
                          <strong className="subtit_item">법적고지문구</strong>
                          <TextArea
                            className="box_textarea2 textarea_s"
                            maxLength={60}
                            value={license.recommendLicenseNumber}
                            placeholder="업종에 적합한 법적고지문을 입력하세요."
                            trim
                            disabled={disabled}
                            onValueChange={handleRecommendLicenseNumberChange}
                            error={errors?.recommendLicenseNumber}
                          />
                          <ErrorText error={errors?.recommendLicenseNumber} />
                          <strong className="subtit_item">소재심사서류</strong>
                          <div className="single_row">
                            <SelectBox
                              placeholder="선택된 파일이 없습니다."
                              value={license.recommendLicenseDocument}
                              disabled={disabled}
                              noBlurOnDefault={
                                !isNil(license.recommendLicenseDocument)
                              }
                              onValueChange={
                                handleRecommendLicenseDocumentChange
                              }
                              error={errors?.recommendLicenseDocument}>
                              {channel?.reviewDocuments
                                .filter(
                                  ({ reviewDocumentType }) =>
                                    reviewDocumentType === 'CREATIVE'
                                )
                                .sort(
                                  (lhs, rhs) =>
                                    Date.parse(rhs.createdDate) -
                                    Date.parse(lhs.createdDate)
                                )
                                .map(reviewDocument => (
                                  <SelectBox.Item
                                    key={reviewDocument.id}
                                    value={reviewDocument}
                                    text={reviewDocument.filename}>
                                    <span className="date_option">
                                      <Moment format="YYYY-MM-DD">
                                        {reviewDocument.createdDate}
                                      </Moment>
                                    </span>
                                    {reviewDocument.filename}
                                  </SelectBox.Item>
                                ))}
                            </SelectBox>
                            <NoneAnchor
                              className="btn_gm gm_line"
                              onClick={showDocumentLinkConfirm}>
                              <span className="inner_g">서류등록</span>
                            </NoneAnchor>
                            <ErrorText
                              error={errors?.recommendLicenseDocument}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="group_itemwrap">
                      <div className="tit_itemwrap">
                        <strong className="tit_item">맞춤소재</strong>
                      </div>
                      <div className="desc_item">
                        <div className="box_item">
                          <strong className="subtit_item">법적고지문구</strong>
                          <TextArea
                            className="box_textarea2 textarea_s"
                            maxLength={60}
                            value={license.customLicenseNumber}
                            placeholder="업종에 적합한 법적고지문을 입력하세요."
                            trim
                            disabled={disabled}
                            onValueChange={handleCustomLicenseNumberChange}
                            error={errors?.customLicenseNumber}
                          />
                          <ErrorText error={errors?.customLicenseNumber} />
                          <strong className="subtit_item">소재심사서류</strong>
                          <div className="single_row">
                            <SelectBox
                              placeholder="선택된 파일이 없습니다."
                              value={license.customLicenseDocument}
                              disabled={disabled}
                              noBlurOnDefault={
                                !isNil(license.customLicenseDocument)
                              }
                              onValueChange={handleCustomLicenseDocumentChange}
                              error={errors?.customLicenseDocument}>
                              {channel?.reviewDocuments
                                .filter(
                                  ({ reviewDocumentType }) =>
                                    reviewDocumentType === 'CREATIVE'
                                )
                                .sort(
                                  (lhs, rhs) =>
                                    Date.parse(rhs.createdDate) -
                                    Date.parse(lhs.createdDate)
                                )
                                .map(reviewDocument => (
                                  <SelectBox.Item
                                    key={reviewDocument.id}
                                    value={reviewDocument}
                                    text={reviewDocument.filename}>
                                    <span className="date_option">
                                      <Moment format="YYYY-MM-DD">
                                        {reviewDocument.createdDate}
                                      </Moment>
                                    </span>
                                    {reviewDocument.filename}
                                  </SelectBox.Item>
                                ))}
                            </SelectBox>
                            <NoneAnchor
                              className="btn_gm gm_line"
                              onClick={showDocumentLinkConfirm}>
                              <span className="inner_g">서류등록</span>
                            </NoneAnchor>
                            <ErrorText error={errors?.customLicenseDocument} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="group_itemwrap">
                    <div className="tit_itemwrap">
                      <strong className="tit_item">
                        추천소재/맞춤소재
                        <br />
                        공통
                      </strong>
                    </div>
                    <div className="desc_item">
                      <div className="box_item">
                        <strong className="subtit_item">법적고지문구</strong>
                        <TextArea
                          className="box_textarea2 textarea_s"
                          maxLength={60}
                          value={licenseNumber}
                          placeholder="업종에 적합한 법적고지문을 입력하세요."
                          trim
                          disabled={disabled}
                          onValueChange={handleLicenseNumberChange}
                        />
                        <strong className="subtit_item">소재심사서류</strong>
                        <div className="single_row">
                          <SelectBox
                            placeholder="선택된 파일이 없습니다."
                            value={licenseDocument}
                            disabled={disabled}
                            noBlurOnDefault={!isNil(licenseDocument)}
                            onValueChange={handleLicenseDocumentChange}>
                            {channel?.reviewDocuments
                              .filter(
                                ({ reviewDocumentType }) =>
                                  reviewDocumentType === 'CREATIVE'
                              )
                              .sort(
                                (lhs, rhs) =>
                                  Date.parse(rhs.createdDate) -
                                  Date.parse(lhs.createdDate)
                              )
                              .map(reviewDocument => (
                                <SelectBox.Item
                                  key={reviewDocument.id}
                                  value={reviewDocument}
                                  text={reviewDocument.filename}>
                                  <span className="date_option">
                                    <Moment format="YYYY-MM-DD">
                                      {reviewDocument.createdDate}
                                    </Moment>
                                  </span>
                                  {reviewDocument.filename}
                                </SelectBox.Item>
                              ))}
                          </SelectBox>
                          <NoneAnchor
                            className="btn_gm gm_line"
                            onClick={showDocumentLinkConfirm}>
                            <span className="inner_g">서류등록</span>
                          </NoneAnchor>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DocumentLinkConfirm
        title="심사서류 등록 페이지로 이동"
        onClose={({ isConfirmed }) => {
          if (isConfirmed) {
            history.push(`/${accountId}/documents/add?from=CREATIVE`)
          }
        }}>
        ‘광고자산 관리 &gt; 심사서류 관리’ 페이지로 이동하시겠습니까?
        <br />
        저장되지 않은 모든 내용은 사라집니다.
      </DocumentLinkConfirm>
    </>
  )
}

TalkChannelLegalNotice.propTypes = {
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    customLicenseNumber: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
    customLicenseDocument: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
    recommendLicenseNumber: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
    recommendLicenseDocument: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
  }),
}
export default TalkChannelLegalNotice
